import React, { forwardRef, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import c from "classnames/bind";

import styles from "./pagination-step-trigger.module.scss";
import { type InternalLinkProps, type InternalNoLinkProps, type Link } from "./types";

const cx = c.bind(styles);

export type PaginationStepTriggerProps = Omit<BaseProps, "children"> & {
  linkElement: Link;
  children: ReactNode;
  disabled: boolean;
  onPageChange: (page: number) => void;
  pageToNavigateTo: number;
  isIconOnly: boolean;
} & (InternalLinkProps | InternalNoLinkProps);

const rootClassName = "purpur-pagination-step-trigger";

export const PaginationStepTrigger = forwardRef<HTMLDivElement, PaginationStepTriggerProps>(
  (
    {
      ["aria-label"]: ariaLabel,
      ["data-testid"]: dataTestId = "purpur-pagination-step-trigger",
      linkElement: LinkElement,
      asLink,
      children,
      className,
      disabled,
      hrefGetter,
      onPageChange,
      pageToNavigateTo,
      isIconOnly,
      ...props
    },
    ref
  ) => {
    const classes = cx(className, rootClassName);
    return (
      <div className={classes} data-testid={dataTestId} ref={ref} {...props}>
        {asLink ? (
          disabled ? (
            <span
              aria-label={ariaLabel}
              className={cx(
                `${rootClassName}__trigger-item`,
                `${rootClassName}__trigger-item--step-trigger`,
                `${rootClassName}__trigger-item--secondary`,
                `${rootClassName}__trigger-item--sm`,
                `${rootClassName}__trigger-item--disabled`,
                { [`${rootClassName}__trigger-item--step-trigger--not-icon-only`]: !isIconOnly }
              )}
              data-testid={`${dataTestId}-disabled-link`}
            >
              {children}
            </span>
          ) : (
            <LinkElement
              aria-label={ariaLabel}
              className={cx(
                `${rootClassName}__trigger-item`,
                `${rootClassName}__trigger-item--step-trigger`,
                `${rootClassName}__trigger-item--secondary`,
                `${rootClassName}__trigger-item--sm`,
                { [`${rootClassName}__trigger-item--step-trigger--not-icon-only`]: !isIconOnly }
              )}
              data-testid={`${dataTestId}-link`}
              href={hrefGetter(pageToNavigateTo)}
              onClick={() => onPageChange(pageToNavigateTo)}
            >
              {children}
            </LinkElement>
          )
        ) : (
          <button
            aria-disabled={disabled}
            aria-label={ariaLabel}
            className={cx(
              `${rootClassName}__trigger-item`,
              `${rootClassName}__trigger-item--step-trigger`,
              `${rootClassName}__trigger-item--secondary`,
              `${rootClassName}__trigger-item--sm`,
              {
                [`${rootClassName}__trigger-item--disabled`]: disabled,
                [`${rootClassName}__trigger-item--step-trigger--not-icon-only`]: !isIconOnly,
              }
            )}
            data-testid={`${dataTestId}-button`}
            disabled={disabled}
            onClick={() => onPageChange(pageToNavigateTo)}
            type="button"
          >
            {children}
          </button>
        )}
      </div>
    );
  }
);

PaginationStepTrigger.displayName = "PaginationStepTrigger";
